import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  contact_name: "",
  company_name: "",
  phone: "",
  email: "",
  pickup_address: "",
  delivery_address: "",
  cargo_type: "",
  weight_lbs: "",
  lift_gate_required: true,
  preferred_date: "",
  notes: "",
};

const inputCls =
  "w-full bg-[#1E1511] border border-[#C85A32]/25 rounded-md px-4 py-3 text-sm text-[#F7F2EB] placeholder-[#E6DACB]/35 outline-none transition-[border-color] duration-300 focus:border-[#E89D4B]";

const labelCls = "block font-mono text-[10px] tracking-[0.22em] uppercase text-[#E89D4B] mb-2";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>{label}</label>
    {children}
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote`, {
        ...form,
        weight_lbs: form.weight_lbs ? Number(form.weight_lbs) : null,
      });
      toast.success("Quote request received — dispatch will reach out shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send your request. Please call (602) 669-4590.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-28 sm:py-36">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(232,157,75,0.12) 0%, transparent 60%)" }}
      />
      <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 text-center"
        >
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C85A32] mb-5 flex items-center justify-center gap-3">
            <span className="w-10 h-px bg-[#C85A32]" />
            06 // Request a Quote
            <span className="w-10 h-px bg-[#C85A32]" />
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F7F2EB] leading-tight">
            Tell us what's moving. <span className="italic text-[#E89D4B]">We'll handle the rest.</span>
          </h2>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="bg-[#2E211A]/80 backdrop-blur-xl border border-[#C85A32]/30 rounded-2xl p-7 sm:p-12 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Contact Name *" htmlFor="quote-name">
              <input id="quote-name" data-testid="quote-input-name" required value={form.contact_name} onChange={set("contact_name")} placeholder="Jane Cargill" className={inputCls} />
            </Field>
            <Field label="Company" htmlFor="quote-company">
              <input id="quote-company" data-testid="quote-input-company" value={form.company_name} onChange={set("company_name")} placeholder="Cargill Supply Co." className={inputCls} />
            </Field>
            <Field label="Phone *" htmlFor="quote-phone">
              <input id="quote-phone" data-testid="quote-input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(602) 555-0100" className={inputCls} />
            </Field>
            <Field label="Email *" htmlFor="quote-email">
              <input id="quote-email" data-testid="quote-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} />
            </Field>
            <Field label="Pickup Address / City *" htmlFor="quote-pickup">
              <input id="quote-pickup" data-testid="quote-input-pickup" required value={form.pickup_address} onChange={set("pickup_address")} placeholder="2100 W Sherman St, Phoenix, AZ" className={inputCls} />
            </Field>
            <Field label="Delivery Address / City *" htmlFor="quote-delivery">
              <input id="quote-delivery" data-testid="quote-input-delivery" required value={form.delivery_address} onChange={set("delivery_address")} placeholder="890 N Scottsdale Rd, Scottsdale, AZ" className={inputCls} />
            </Field>
            <Field label="Cargo Type *" htmlFor="quote-cargo">
              <input id="quote-cargo" data-testid="quote-input-cargo" required value={form.cargo_type} onChange={set("cargo_type")} placeholder="2 pallets of restaurant equipment" className={inputCls} />
            </Field>
            <Field label="Estimated Weight (lbs)" htmlFor="quote-weight">
              <input id="quote-weight" data-testid="quote-input-weight" type="number" min="0" max="100000" value={form.weight_lbs} onChange={set("weight_lbs")} placeholder="2,400" className={inputCls} />
            </Field>
            <Field label="Lift Gate Required?" htmlFor="quote-liftgate-yes">
              <div className="grid grid-cols-2 gap-3" role="group" aria-label="Lift gate required">
                <button
                  type="button"
                  id="quote-liftgate-yes"
                  data-testid="quote-liftgate-yes"
                  onClick={() => setForm((f) => ({ ...f, lift_gate_required: true }))}
                  className={`rounded-md px-4 py-3 font-mono text-xs tracking-[0.18em] uppercase border transition-colors duration-300 ${
                    form.lift_gate_required
                      ? "bg-[#C85A32] border-[#C85A32] text-[#F7F2EB]"
                      : "bg-[#1E1511] border-[#C85A32]/25 text-[#E6DACB]/70 hover:border-[#E89D4B]"
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="quote-liftgate-no"
                  onClick={() => setForm((f) => ({ ...f, lift_gate_required: false }))}
                  className={`rounded-md px-4 py-3 font-mono text-xs tracking-[0.18em] uppercase border transition-colors duration-300 ${
                    !form.lift_gate_required
                      ? "bg-[#C85A32] border-[#C85A32] text-[#F7F2EB]"
                      : "bg-[#1E1511] border-[#C85A32]/25 text-[#E6DACB]/70 hover:border-[#E89D4B]"
                  }`}
                >
                  No
                </button>
              </div>
            </Field>
            <Field label="Preferred Date & Time" htmlFor="quote-date">
              <input id="quote-date" data-testid="quote-input-date" type="datetime-local" value={form.preferred_date} onChange={set("preferred_date")} style={{ colorScheme: "dark" }} className={inputCls} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes" htmlFor="quote-notes">
                <textarea id="quote-notes" data-testid="quote-input-notes" rows={4} value={form.notes} onChange={set("notes")} placeholder="Access instructions, dock hours, fragile items…" className={`${inputCls} resize-none`} />
              </Field>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#E6DACB]/50 text-center sm:text-left">
              Same-day slots fill fast — or call <a href="tel:+16026694590" data-testid="quote-phone-link" className="text-[#E89D4B] hover:text-[#F7F2EB] transition-colors duration-300">(602) 669-4590</a>
            </p>
            <button
              type="submit"
              data-testid="quote-submit-button"
              disabled={sending}
              className="group flex items-center gap-2 bg-[#C85A32] text-[#F7F2EB] font-mono text-xs tracking-[0.18em] uppercase rounded-full px-10 py-4 hover:bg-[#E89D4B] hover:text-[#1E1511] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send Quote Request"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
